/** @jsxImportSource @emotion/react */

import React, { useEffect, useState } from "react";
import axios from "axios";
import { storage } from "../utils/storage";
import UIButton from "../components/Button";
import { formatTimeFromNow } from "../utils/formatTime";
import { useNavigate } from "react-router-dom";
import useAIModels from "../hooks/useAIModels";
import { css } from "@emotion/react";
const baseUrl = process.env.REACT_APP_API_CONFIG;

// Helper to strip HTML tags from message
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  // Remove HTML tags and decode common HTML entities
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

// Helper to format last message for display
const formatLastMessage = (lastMessage: any): string => {
  if (!lastMessage) return "No message";

  const message = lastMessage.message || "";
  const messageType = lastMessage.message_type;

  // Check if it's an image message
  if (messageType === "image") {
    return "🖼️ Generated image";
  }

  // Check if message looks like a URL (image generation result)
  if (message.match(/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp)/i) ||
      message.includes('/uploads/generated/')) {
    return "🖼️ Generated image";
  }

  // Strip HTML tags if present (messages from bot may contain HTML)
  const cleanMessage = stripHtmlTags(message);

  return cleanMessage || "No message";
};

const Recent = () => {
  const [conversations, setConversations] = useState<any[]>([]);
  const navigate = useNavigate();
  const { models } = useAIModels();
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      setSelectedModel(models[0]);
    }
  }, [models, selectedModel]);

  const handleNewChat = () => {
    const token = storage.getJwtToken();
    if (!token) {
      navigate("/get-started");
      return;
    }

    const modelToUse = selectedModel || (models.length > 0 ? models[0] : null);
    if (modelToUse) {
      navigate(`/chat/${modelToUse.id}`);
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    const token = storage.getJwtToken();
    if (!token) {
      navigate("/get-started");
      return;
    }

    const fetchConversations = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/chat/conversation?page=1&pageSize=10`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setConversations(response.data.data || []);
      } catch (error) {
        console.error("Failed to fetch conversations", error);
      }
    };

    fetchConversations();
  }, [navigate]);

  return (
    <div className="flex flex-col h-full min-h-0 justify-between relative">
      <div className="relative"></div>

      {/* Recent Chats Area */}
      <div
        className={`flex flex-col flex-1 overflow-y-auto w-full min-h-0 ${
          isMobile || isTablet ? "px-4" : "px-52"
        }`}>
        {conversations.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-chat bg-black">
            <img
              src="/assets/images/icon-empty-box.svg"
              alt="No chats"
              className="w-16 h-16"
            />
            <p className="text-base font-bold text-center text-chat mt-3">
              No chat found
            </p>
            <p className="text-color-desc text-center mb-4">
              Let's ask me the first question
            </p>
            <UIButton
              text="Start Chat"
              textColor={""}
              onClick={handleNewChat}
              backgroundColor="btn-color w-40"
            />
          </div>
        ) : (
          <div
            className="flex flex-col flex-1 w-full space-y-4 py-4"
            css={css`
              @media (max-width: 1024px) {
                padding-top: 80px;
              }
            `}>
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className="bg-grey rounded-xl p-4 cursor-pointer"
                onClick={() =>
                  navigate(`/chat/conversation/${conv.documentId}`)
                }>
                <div className="flex justify-between items-start">
                  <div className="flex gap-2 mb-2 items-center">
                    <img src="/assets/images/icon-clock.svg" alt="" />
                    <span className="text-xs tracking-wide uppercase text-color-premium-desc">
                      {formatTimeFromNow(conv.last_message_at || conv.updatedAt)}
                    </span>
                  </div>
                  <img
                    src="/assets/images/icon-arrow-recent-chat.svg"
                    alt=""
                    className="cursor-pointer"
                  />
                </div>
                <p className="sm:text-base md:text-lg text-white font-medium mb-1 line-clamp-1">
                  {conv.title || "Untitled"}
                </p>
                <p className="sm:text-sm md:text-sm text-color-desc line-clamp-2">
                  {formatLastMessage(conv.lastMessage)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Recent;
